/* eslint-disable @typescript-eslint/no-explicit-any */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { notFound, redirect } from "@tanstack/react-router";
import {
  useAuth,
  useVenueDetailsRoute,
  useBusinessInterests,
  useI18N,
} from "common/hooks";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useGeoLocation } from "@/hooks/useGeoLocation";
import { defaultRegion, bucketUrlPrefix } from "common/config";
import { en as enLocalizations } from "common/config/localizations";
import {
  getVenuePageTitle,
  getVenueLocationSubtitle,
  pinTypeForVenueDetails,
} from "common/utils";
import VenueSheetContent from "@/components/VenueDetailBasic/VenueSheetContent";
import ComplexVenueSheetContent from "@/components/VenueDetailComplex/ComplexVenueSheetContent";
import InterestNotificationModal from "@/components/Notifications/NotificationModal/InterestNotificationModal";
import Loader from "@/components/common/Loader";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import { Storage } from "common/storage";
import { trackEvent } from "common/analytics";
import { useNearbyBusinesses } from "common/hooks/business/useNearbyBusinesses";
import { useSearchPageParams } from "common/hooks";
import { mapCenterAtom } from "@/state/atoms";
import { useAtom } from "jotai";
import VenueAssistantMapDock from "@/components/VenueDetailBasic/VenueAssistantMapDock";
import BasicMapView from "@/components/VenueDetailBasic/BasicMapView";
import {
  cn,
  getVenueSlugs,
  passwordTokenStorageKey,
  getStoredPasswordToken,
  ssoTokenStorageKey,
  consumeIncomingSsoToken,
  buildSsoRedirectUrl,
} from "@/lib/utils";
import { useSelectedVenue } from "@/hooks/useSelectedVenue";
import { venueFromDetail, Venue, BusinessInterestType } from "common/types";
import { getPinIcon } from "common/utils";
import PhotosDetailView from "@/components/VenueDetailBasic/PhotosDetailView";
import AccessibilityDetailView from "@/components/VenueDetailBasic/AccessibilityDetailView";
import PasswordGate from "@/components/PasswordGate/PasswordGate";
import { ClientOnly } from "@/components/common/ClientOnly";
import { VenueDetailsErrorBoundary } from "@/components/venuedetails/VenueDetailsErrorBoundary";
import { VenueDetailsFallback } from "@/components/venuedetails/VenueDetailsFallback";
import { venueDetailsRouteQueryOptions } from "common/hooks/business/details/venueDetailsQueryOptions";
import { NotFoundError, SlugRedirectError } from "common/apis/venueDetails";
import { slugToTitle } from "@/lib/slug";
import { sanitiseMetaDescription } from "@/lib/meta";
import { buildPlaceSchema } from "@/lib/placeSchema";

const isProduction =
  import.meta.env.VITE_APPLICATION_ENVIRONMENT === "Production";

export type VenueDetailsRouteParams = {
  venueId: string;
};

export type VenueDetailsRouteSearch = {
  source?: "map" | "activity" | "notifications-page" | "notification-settings";
  showAuthModal?: boolean;
  cid?: string;
  tid?: string;
  tb?: string;
  tab?: string;
  openModal?: boolean;
  /** Auto-open the accessibility/live-alert notification modal (from the map "Get notified" CTA). */
  notify?: boolean;
  token?: string;
  /** 1-based position of the home card the user clicked — threaded into place_viewed.entry_index. */
  ei?: number;
  /** Set by the embeddable widget's CTAs — place_viewed's entry_type. */
  entry_type?: "widget";
  /** The widget's host page (origin + path) — place_viewed's entry_page_url. */
  entry_url?: string;
};

/**
 * Resolve place_viewed's entry_page / entry_type. Prefer the explicit search params (`entry_type`
 * from the widget, `source` from map / notification navigations); otherwise classify from
 * document.referrer. Both are constrained to the tracking plan's controlled vocab (entry_page:
 * home | venue_search_result_list | map_venue_preview | saved_list | notification | venue | "";
 * entry_type: internal | external_link | direct | widget). An unrecognised same-origin referrer
 * yields an empty entry_page (best-effort, still valid).
 */
function resolveVenueEntryContext(
  source: VenueDetailsRouteSearch["source"],
  referrer: string,
  entryType: VenueDetailsRouteSearch["entry_type"],
): { entry_page: string; entry_type: string } {
  if (source === "map")
    return { entry_page: "map_venue_preview", entry_type: "internal" };
  if (source === "notifications-page" || source === "notification-settings")
    return { entry_page: "notification", entry_type: "internal" };
  // Widget CTAs are cross-origin and rel="noreferrer", so the referrer below is always
  // empty here. entry_page stays "" — the plan's entry_page vocab has no widget value.
  if (entryType === "widget") return { entry_page: "", entry_type: "widget" };

  // No referrer => typed URL / bookmark / new tab.
  if (!referrer) return { entry_page: "", entry_type: "direct" };

  let path = "";
  let sameOrigin = false;
  try {
    const url = new URL(referrer);
    path = url.pathname;
    sameOrigin =
      typeof window !== "undefined" && url.origin === window.location.origin;
  } catch {
    /* malformed referrer — treat as external */
  }

  if (!sameOrigin) return { entry_page: "", entry_type: "external_link" };

  let entry_page = "";
  if (path === "/") entry_page = "home";
  else if (path.startsWith("/search")) entry_page = "venue_search_result_list";
  else if (
    path.startsWith("/mysavedplaces") ||
    path.startsWith("/savedplacedetails")
  )
    entry_page = "saved_list";
  else if (path.startsWith("/notifications")) entry_page = "notification";
  else if (path.startsWith("/venuedetails")) entry_page = "venue";
  return { entry_page, entry_type: "internal" };
}

export const Route = createFileRoute("/venuedetails/$venueId")({
  ssr: true,
  component: VenueDetails,
  errorComponent: VenueDetailsErrorBoundary,
  validateSearch: (
    search: Record<string, unknown>,
  ): VenueDetailsRouteSearch => {
    const showAuthModal =
      search.showAuthModal === "true" || search.showAuthModal === true;
    const openModal = search.openModal === "true" || search.openModal === true;
    const notify = search.notify === "true" || search.notify === true;
    return {
      source: search.source
        ? (search.source as
            | "map"
            | "activity"
            | "notifications-page"
            | "notification-settings")
        : undefined,
      showAuthModal: showAuthModal ? true : undefined,
      cid: search.cid as string,
      tid: search.tid as string,
      tb: search.tb as string,
      tab: search.tab as string,
      openModal: openModal ? true : undefined,
      notify: notify ? true : undefined,
      token: search.token as string | undefined,
      ei:
        search.ei != null && Number.isFinite(Number(search.ei))
          ? Number(search.ei)
          : undefined,
      // Whitelisted, not cast: entry_type feeds a controlled-vocab analytics property
      // straight from a URL anyone can edit.
      entry_type: search.entry_type === "widget" ? "widget" : undefined,
      entry_url:
        typeof search.entry_url === "string" ? search.entry_url : undefined,
    };
  },
  parseParams: (params: Record<string, string>) => {
    return {
      venueId: params.venueId,
    };
  },
  loader: async ({ params, context, location }) => {
    const queryOpts = venueDetailsRouteQueryOptions({
      slug: params.venueId,
      latitude: defaultRegion.latitude.toString(),
      longitude: defaultRegion.longitude.toString(),
      accessToken: undefined,
    });
    let venue = null;
    try {
      venue = await context.queryClient.ensureQueryData(queryOpts);
    } catch (err) {
      if (err instanceof NotFoundError) throw notFound();
      // Retired slug → permanent redirect to the current canonical URL, preserving the
      // page's search params. Runs server-side (real HTTP 301 via serve.js) and on
      // client-side navigations to an old slug.
      if (err instanceof SlugRedirectError)
        throw redirect({
          to: `/venuedetails/${err.canonicalSlug}`,
          // Preserve the page's current search params (cid/tid/tab/token/…). Cast per
          // the typed-redirect limitation, matching venuephotos.$venueSlug.tsx.
          search: location.search as any,
          statusCode: 301,
        });
      // SSR graceful-degrade: render the slug-derived fallback shell
      // (title/description/canonical/JSON-LD shell) instead of failing
      // the route. Critical: clear the error-state cache entry that
      // ensureQueryData leaves behind so the client hydrates with no
      // entry for this key — useVenueDetailsBySlug then runs queryFn
      // fresh on next mount/trigger. Without this, refetchOnMount:
      // false + a hydrated error state would block recovery.
      context.queryClient.removeQueries({
        queryKey: queryOpts.queryKey,
        exact: true,
      });
      console.error(
        "[venue loader] fetch failed; rendering fallback shell",
        err,
      );
      venue = null;
    }
    return {
      venue,
      canonicalOrigin:
        (typeof process !== "undefined" && process.env?.PUBLIC_ORIGIN) || "",
    };
  },
  head: ({ loaderData, params }) => {
    const venue = loaderData?.venue?.details;
    const origin = loaderData?.canonicalOrigin ?? "";
    const fallbackName = slugToTitle(params.venueId);
    const titleName = venue?.name ?? fallbackName;
    const title = getVenuePageTitle(
      {
        name: titleName,
        route: venue?.route,
        sublocality: venue?.sublocality,
        postalTown: venue?.postalTown,
        locality: venue?.locality,
        cityLocation: venue?.cityLocation,
        address: venue?.address,
      },
      enLocalizations.venuePageTitleSuffix,
    );
    // The generic template is identical for every venue, which reads as duplicate
    // content at estate scale; venue_summary is unique and already loaded.
    const summary = venue?.business?.venue_summary?.trim();
    const location = getVenueLocationSubtitle({
      sublocality: venue?.sublocality,
      route: venue?.route,
      postalTown: venue?.postalTown,
      locality: venue?.locality,
      cityLocation: venue?.cityLocation,
      address: venue?.address,
    });
    const description = sanitiseMetaDescription(
      summary
        ? `${titleName}${location ? `, ${location}` : ""}. ${summary}`
        : `${enLocalizations.venueMetaDescriptionPrefix} ${titleName} ${enLocalizations.venueMetaDescriptionSuffix}`,
    );
    return {
      meta: [
        { title },
        { name: "description", content: description },
        ...(isProduction ? [{ name: "robots", content: "index, follow" }] : []),
      ],
      links: [
        { rel: "canonical", href: `${origin}/venuedetails/${params.venueId}` },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildPlaceSchema({
              venue,
              basic: loaderData?.venue?.accesibilityCategories,
              enterprise: loaderData?.venue?.accesibilityCategoriesEnterprise,
              origin,
              slug: params.venueId,
            }),
          ),
        },
      ],
    };
  },
});

function VenueDetails() {
  const { venueId: venueSlug } = Route.useParams();
  const loaderData = Route.useLoaderData();
  const searchParams = Route.useSearch();
  const {
    showAuthModal,
    cid,
    tid,
    tb,
    tab,
    openModal,
    notify,
    token: urlSsoToken,
  } = searchParams;
  const source = searchParams.source;
  // 1-based home-card position threaded via ?ei — place_viewed's entry_index (0 for
  // direct / search / saved / external entries that don't carry it).
  const entryIndexFromNav = searchParams.ei;
  const entryTypeFromNav = searchParams.entry_type;
  const entryUrlFromNav = searchParams.entry_url;
  const i18n = useI18N();

  const { user, isLoading, isFetching, isLoggedIn } = useAuth();
  const isAuthLoading = isLoading || isFetching;

  const [showAuthModalState, setShowAuthModalState] = useState(showAuthModal);
  const { location } = useGeoLocation();
  const { setSearchPageParams } = useSearchPageParams();
  const [, setMapCenter] = useAtom(mapCenterAtom);
  const [currentVenueTypePage] = useState(0);
  const { selectedVenue, setSelectedVenue } = useSelectedVenue();
  const navigate = useNavigate();
  const isSyncingFromUrl = useRef(false);

  // Password state (Complex venues only)
  const [passwordToken, setPasswordToken] = useState<string | undefined>(() =>
    getStoredPasswordToken(venueSlug),
  );
  const [pendingPassword, setPendingPassword] = useState<string | undefined>();
  const [passwordError, setPasswordError] = useState(false);

  const [ssoToken, setSsoToken] = useState<string | undefined>(() =>
    consumeIncomingSsoToken(venueSlug, urlSsoToken),
  );
  const hasSsoRedirected = useRef(false);

  // Tracks the venue (by business id) place_viewed has already fired for, so StrictMode's double
  // mount fires once while a map-driven navigation to a *different* venue still re-fires.
  const placeViewedKeyRef = useRef<string | null>(null);

  // State for AccessibilityDetailView
  const [showAccessibilityDetail, setShowAccessibilityDetail] = useState(false);
  const [accessibilityDetailSpaceId, setAccessibilityDetailSpaceId] =
    useState<string>("");

  // State for PhotosDetailView
  const [showPhotosDetail, setShowPhotosDetail] = useState(false);
  const [photosDetailCategoryId, setPhotosDetailCategoryId] = useState<
    number | null
  >(null);

  // State for notification modal (used by complex venues)
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [selectedVenueForNotification, setSelectedVenueForNotification] =
    useState<{
      id: string;
      name: string;
      brandId?: string;
      brandName?: string;
      interestId?: string;
      interestedType?: string;
      notifyAccessibilityInformation: boolean;
      notifyLiveAlert: boolean;
    } | null>(null);

  // load this venue from the server
  const {
    data,
    error,
    refetch: loadVenueDetails,
    isLoading: isDetailsLoading,
    isPlaceholderData,
  } = useVenueDetailsRoute({
    slug: venueSlug,
    latitude:
      location?.latitude.toString() || defaultRegion.latitude.toString(),
    longitude:
      location?.longitude.toString() || defaultRegion.longitude.toString(),
    accessToken: Storage.getCurrentUserToken(),
    enabled: location ? true : false,
    password: pendingPassword,
    passwordToken,
    ssoToken,
    // The loader fetches anonymously, so a gated payload would flash the password gate —
    // or fire the SSO redirect below — at a user who actually holds a valid token.
    placeholderData:
      loaderData?.venue?.canViewPage === 0 ? undefined : loaderData?.venue,
  });

  const { showModal } = useAuthModals();

  // Function to update URL with space parameters
  const updateVenueDetailsUrl = (spaceId?: string | number) => {
    console.log("updateVenueDetailsUrl", spaceId);
    console.log("venueSlug", venueSlug);
    console.log("data", data);

    if (spaceId !== undefined && spaceId !== null) {
      // Convert to string and ensure it's clean without quotes
      const cleanSpaceId = String(spaceId).replace(/['"]/g, "");
      navigate({
        to: `/accessibility-guide-space/${venueSlug}/${cleanSpaceId}`,
        replace: false, // Don't replace - add to history for proper back button behavior
      });
    } else {
      // Navigate back to the main accessibility guide page (no space selected)
      navigate({
        to: `/venuedetails/${venueSlug}`,
        replace: false,
      });
    }
  };

  // Add nearby businesses query
  const { data: nearbyBusinessesData, isLoading: isLoadingNearbyBusinesses } =
    useNearbyBusinesses(data?.details?.business.id.toString());

  // Get business interests to check which venues have notifications enabled (for complex venues)
  const { data: businessInterests } = useBusinessInterests(false);

  // Create a Map for quick lookup of business interest details (for updates)
  const businessInterestMap = useMemo(() => {
    if (!businessInterests) return new Map<number, BusinessInterestType>();

    return new Map<number, BusinessInterestType>(
      businessInterests.map(
        (interest) =>
          [interest.business_id, interest] as [number, BusinessInterestType],
      ),
    );
  }, [businessInterests]);

  const hasAccessibilityData = useMemo(() => {
    return (
      (data?.accesibilityCategories?.length ?? 0) > 0 ||
      (data?.accesibilityCategoriesHybrid?.length ?? 0) > 0 ||
      (data?.accesibilityCategoriesEnterprise?.length ?? 0) > 0
    );
  }, [
    data?.accesibilityCategories,
    data?.accesibilityCategoriesHybrid,
    data?.accesibilityCategoriesEnterprise,
  ]);

  // Add hasNearbyVenues constant
  const hasNearbyVenues = useMemo(() => {
    if (isLoadingNearbyBusinesses) return false;
    // Use templateId > 1 to check for complex venues (not Basic)
    return (
      data?.details?.business?.templateId != null &&
      data?.details?.business?.templateId > 1 &&
      nearbyBusinessesData?.data?.nearbyBusinessCategories?.length > 0
    );
  }, [
    isLoadingNearbyBusinesses,
    data?.details?.business?.templateId,
    nearbyBusinessesData,
  ]);

  // Determine if this is a complex venue
  // Use templateId > 1 as the indicator (same logic as used throughout the app)
  // templateType can be empty string for some complex venues
  const isComplexVenue = useMemo(() => {
    return (
      data?.details?.business?.templateId != null &&
      data?.details?.business?.templateId > 1
    );
  }, [data?.details?.business?.templateId]);

  // Redirect to what's-nearby if no accessibility data but has nearby venues.
  // Skip when:
  //   - canViewPage === 0: access is denied (password gate or SSO redirect handles it).
  //   - passwordToken is set: the user authenticated via password.
  //   - ssoToken is set: the user authenticated via SSO.
  useEffect(() => {
    if (
      !hasAccessibilityData &&
      hasNearbyVenues &&
      data?.details?.business?.slug &&
      data?.canViewPage !== 0 &&
      !passwordToken &&
      !ssoToken
    ) {
      const { contentSlug } = getVenueSlugs(data.details);
      window.location.href = `/whats-nearby/${contentSlug}`;
    }
  }, [
    hasAccessibilityData,
    hasNearbyVenues,
    data?.details?.business?.slug,
    data?.details?.businessSlugDetails,
    data?.canViewPage,
    passwordToken,
    ssoToken,
  ]);

  // Memoize loadVenueDetails to prevent infinite loops
  const loadVenueDetailsRef = useRef(loadVenueDetails);
  loadVenueDetailsRef.current = loadVenueDetails;

  const memoizedLoadVenueDetails = useCallback(() => {
    loadVenueDetailsRef.current();
  }, []);

  // Method to handle closing accessibility detail view
  const handleCloseAccessibilityDetail = useCallback(() => {
    setShowAccessibilityDetail(false);
    setAccessibilityDetailSpaceId("");

    // Clean up URL parameters if they exist
    if (cid && tid) {
      const url = new URL(window.location.href);
      url.searchParams.delete("cid");
      url.searchParams.delete("tid");
      window.history.pushState({}, "", url.toString());
      // Navigate back to venue details
      window.location.href = `/venuedetails/${venueSlug}`;
    }
  }, [data?.details, cid, tid, venueSlug]);

  // Handler for notification click (used by complex venues)
  const handleNotificationClick = useCallback(
    (venue: Venue) => {
      // place_notification_cta_activated — chokepoint for the complex venue's own notification CTA
      // (the bell buttons in ComplexVenueSheetContent both route here). Fire before the login branch
      // so the CTA activation is captured regardless of auth state.
      trackEvent("place_notification_cta_activated", {
        brand_id: venue.brandId != null ? String(venue.brandId) : "",
        brand_name: venue.brandName ?? "",
        business_id: venue.business?.id?.toString() ?? "",
        pin_type: pinTypeForVenueDetails(data?.details),
        place_name: venue.name,
      });

      if (!isLoggedIn) {
        showModal(AuthModalScreen.EnterEmail);
        return;
      }

      const businessId = venue.business?.id;

      // Check if this venue already has an interest record
      const existingInterest: BusinessInterestType | undefined = businessId
        ? businessInterestMap.get(businessId)
        : undefined;

      setSelectedVenueForNotification({
        id: businessId?.toString() || venue.id.toString(),
        name: venue.name,
        brandId: venue.brandId != null ? String(venue.brandId) : "",
        brandName: venue.brandName ?? "",
        interestId: existingInterest?.id.toString(),
        interestedType: existingInterest?.interested_type,
        notifyAccessibilityInformation:
          existingInterest?.notify_accessibility_information === 1,
        notifyLiveAlert: existingInterest?.notify_live_alert === 1,
      });
      setIsNotificationModalOpen(true);
    },
    [isLoggedIn, showModal, businessInterestMap, data?.details?.to_be_verified],
  );

  const handleNotificationModalClose = useCallback(() => {
    // Only close the interest modal, but keep venue data
    // This allows the success modal inside InterestNotificationModal to still render
    // The venue data will be cleared when opening a new notification modal
    setIsNotificationModalOpen(false);
  }, []);

  useEffect(() => {
    if (location && !isAuthLoading) {
      memoizedLoadVenueDetails();
    }
  }, [location, memoizedLoadVenueDetails, isAuthLoading]);

  useEffect(() => {
    if (showAuthModalState && !user && !isAuthLoading) {
      showModal(AuthModalScreen.EnterEmail);
      setShowAuthModalState(false);
    }
  }, [showAuthModalState, user, isAuthLoading, showModal]);

  // When the SAML backend redirects back with ?token= in the URL:
  // store it in localStorage and strip it from the address bar.
  useEffect(() => {
    if (urlSsoToken) {
      localStorage.setItem(ssoTokenStorageKey(venueSlug), urlSsoToken);
      setSsoToken(urlSsoToken);
      // Clean ?token= from the URL so it doesn't linger in browser history
      const url = new URL(window.location.href);
      url.searchParams.delete("token");
      window.history.replaceState({}, "", url.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-redirect to SSO login when the venue is SSO-protected and no token is present.
  const ssoRedirectUrl = useMemo(() => {
    if (data?.isSsoProtect === 1 && data?.ssoTenantId && data?.details) {
      const { venueDetailsSlug } = getVenueSlugs(data.details);
      return buildSsoRedirectUrl(data.ssoTenantId, venueDetailsSlug);
    }
    return undefined;
  }, [data?.isSsoProtect, data?.ssoTenantId, data?.details]);

  useEffect(() => {
    if (
      ssoRedirectUrl &&
      data?.canViewPage === 0 &&
      !ssoToken &&
      !hasSsoRedirected.current
    ) {
      hasSsoRedirected.current = true;
      window.location.href = ssoRedirectUrl;
    }
  }, [ssoRedirectUrl, data?.canViewPage, ssoToken]);

  // Handle password gate response for Complex venues
  useEffect(() => {
    if (!data?.details || !pendingPassword) return;

    if (data.canViewPage === 1 && data.passwordProtectedPageToken) {
      // Password was correct — store the token and clear the pending password
      const token = data.passwordProtectedPageToken;
      localStorage.setItem(passwordTokenStorageKey(venueSlug), token);
      setPasswordToken(token);
      setPendingPassword(undefined);
      setPasswordError(false);
    } else if (data.canViewPage === 0) {
      // Password was wrong
      setPasswordError(true);
      setPendingPassword(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.details]);

  // NOTE: Redirect logic removed - now handling both venue types in a single route
  // Previously redirected Complex/Hybrid venues to /complexvenuedetails

  // Set map center and search params based on venue coordinates
  useEffect(() => {
    if (data?.details) {
      const venueLat = data.details.latitude;
      const venueLng = data.details.longitude;

      // Set map center atom
      setMapCenter({ lat: venueLat, lng: venueLng });

      // Update searchPageParams with venue location for map operations
      // This ensures BasicMapView uses the correct location context
      setSearchPageParams((prev) => ({
        ...prev,
        latitude: venueLat.toString(),
        longitude: venueLng.toString(),
        placesToReplaceString: data.details.address || data.details.name,
        unixTimestamp: Math.floor(Date.now() / 1000),
      }));

      // Convert venue details to Venue object and set as selected venue
      // This will make the map highlight the active marker for this venue.
      // venueFromDetail doesn't know the pin colour — derive it the same way
      // the map/search results do, so the selected pin (and its verified
      // tick) reflect the venue's real mapped/verified state, not always grey.
      const venue = venueFromDetail(data.details);
      venue.venuePinType = getPinIcon(venue);

      // Set flag to prevent navigation when data loads from URL change (back/forward button)
      // Only navigate when user actively clicks a different marker.
      // Also treat alternate/cohort slugs as "syncing from URL" so we don't redirect away.
      const isAlternateSlug =
        data.details.businessSlugDetails?.slug === venueSlug &&
        venue.business.slug !== venueSlug;

      if (venue.business.slug === venueSlug || isAlternateSlug) {
        // Venue matches URL (either by main slug or an alternate/cohort slug)
        isSyncingFromUrl.current = true;
      }
      setSelectedVenue(venue);
    }
  }, [
    data?.details,
    setMapCenter,
    setSearchPageParams,
    setSelectedVenue,
    location,
    venueSlug,
  ]);

  // Navigate to the selected venue when a different marker is clicked on the map
  useEffect(() => {
    // Skip if we're syncing from URL (to avoid duplicate navigation)
    if (isSyncingFromUrl.current) {
      isSyncingFromUrl.current = false;
      return;
    }

    // Only navigate if:
    // 1. There's a selected venue
    // 2. It's a different venue from the currently loaded one (compare by slug)
    // 3. The data has already been loaded (to avoid racing with initial load)
    // Also suppress navigation if the URL slug is an alternate/cohort slug for
    // the loaded venue — this check is stateless so it survives multiple effect runs
    const isAlternateSlug =
      !!data?.details?.businessSlugDetails?.slug &&
      data.details.businessSlugDetails.slug === venueSlug;

    if (
      selectedVenue &&
      data?.details &&
      selectedVenue.business?.slug &&
      selectedVenue.business.slug !== venueSlug &&
      !isAlternateSlug
    ) {
      navigate({ to: `/venuedetails/${selectedVenue.business.slug}` });
    }
  }, [selectedVenue, data?.details, venueSlug, navigate]);

  // place_viewed — the "View Venue" flow's page-view event. Fired with the trackEvent primitive,
  // NOT createPageAnalytics("place").track("page_view_page") — the "place" page_view_page element is
  // shared with the widget + alert-vote surfaces (place_viewed, vote_alert_form_opened, widget_loaded,
  // widget_opened), so the page-analytics path would over-fire. Ref-guarded by business id so it fires
  // once per venue. Skipped while access is gated (canViewPage === 0: password / SSO redirect).
  useEffect(() => {
    const details = data?.details;
    if (!details || data?.canViewPage === 0) return;
    const key = details.business?.id?.toString() ?? venueSlug;
    if (placeViewedKeyRef.current === key) return;
    placeViewedKeyRef.current = key;

    const referrer = typeof document !== "undefined" ? document.referrer : "";
    const { entry_page, entry_type } = resolveVenueEntryContext(
      source,
      referrer,
      entryTypeFromNav,
    );

    trackEvent("place_viewed", {
      business_id: details.business?.id?.toString() ?? "",
      // brandId comes back as a number (e.g. 25); the plan's brand_id is a string.
      brand_id: details.brandId != null ? String(details.brandId) : "",
      brand_name: details.brandName ?? "",
      place_name: details.name ?? "",
      place_address: details.address ?? "",
      place_category: details.businessType ?? "",
      place_template_type: isComplexVenue ? "complex" : "basic",
      pin_type: pinTypeForVenueDetails(details),
      entry_page,
      entry_page_url: entryUrlFromNav || referrer,
      entry_type,
      entry_index: entryIndexFromNav ?? 0,
    });
  }, [
    data?.details,
    data?.canViewPage,
    isComplexVenue,
    source,
    venueSlug,
    entryIndexFromNav,
    entryTypeFromNav,
    entryUrlFromNav,
  ]);

  // Any dataless render — SSR loader failure or a client fetch still in flight — ships the
  // slug-derived shell, so the document always carries an <h1> rather than a bare spinner.
  if (!data && !error) {
    return (
      <>
        {/* The loader's summary is present on the flash path (client fetch in flight) and
            absent on the failure path, where the slug-derived sentence takes over. */}
        <VenueDetailsFallback
          slug={venueSlug}
          summary={loaderData?.venue?.details?.business?.venue_summary}
        />
        <ClientOnly>
          {(isAuthLoading || isDetailsLoading) && <Loader />}
        </ClientOnly>
      </>
    );
  }

  return (
    <main
      id="main"
      className={cn(
        "bg-bg-brand-secondary-default",
        !isComplexVenue &&
          "max-w-[1440px] mx-auto overflow-hidden h-below-navbar",
      )}
    >
      {/* Client-only: auth is always pending server-side, so an unguarded Loader
          puts a focus-trapped overlay into every SSR response. Skipped while the SSR
          payload stands in — covering readable content with a full-screen trap is worse
          than letting the personalised response swap in underneath. */}
      <ClientOnly>
        {(isAuthLoading || isDetailsLoading) && !isPlaceholderData && <Loader />}
      </ClientOnly>

      {/* Accessibility Detail View - Full screen overlay (Basic venues only) */}
      {data && showAccessibilityDetail && !isComplexVenue && (
        <section className="h-below-navbar bg-bg-brand-secondary-default flex flex-col overflow-hidden">
          <div className="flex-1 min-h-0 overflow-hidden">
            <AccessibilityDetailView
              venueDetails={data.details}
              accessibilityCategories={data.accesibilityCategories || []}
              initialSpaceId={accessibilityDetailSpaceId}
              onClose={handleCloseAccessibilityDetail}
              onShare={(spaceId) => {
                if (navigator.share) {
                  navigator.share({
                    title: data.details.name,
                    text: `Check out accessibility information at ${data.details.name}`,
                    url: `${window.location.origin}/accessibility-guide-space/${data.details.business.slug}/${spaceId}`,
                  });
                }
              }}
            />
          </div>
        </section>
      )}

      {/* Photos Detail View - Full screen overlay */}
      {data && showPhotosDetail && (
        <section className="h-below-navbar bg-bg-brand-secondary-default overflow-hidden">
          <PhotosDetailView
            venueDetailsResponse={data}
            accessibilityCategories={data.accesibilityCategories || []}
            initialCategoryId={photosDetailCategoryId}
            venueName={data.details.name}
            onClose={() => {
              setShowPhotosDetail(false);
              setPhotosDetailCategoryId(null);
            }}
          />
        </section>
      )}

      {/* Regular Venue Details View */}
      {!showAccessibilityDetail && !showPhotosDetail && data && (
        <>
          {/* Complex Venue Layout (no map sidebar) */}
          {isComplexVenue ? (
            <section className="mb-2">
              {/* Access denied — show SSO redirect or password gate */}
              {data.canViewPage === 0 ? (
                <div className="min-h-screen w-full bg-bg-brand-secondary-default">
                  {/* Hero Banner — matches ComplexVenueSheetContent layout */}
                  <div className="relative w-full h-[280px] md:h-[320px] min-w-full">
                    <div className="absolute inset-0 overflow-hidden w-full">
                      {data.details.businessPhotoFirst?.path && (
                        <img
                          alt={data.details.name}
                          className="w-full h-full object-cover min-w-full"
                          src={
                            bucketUrlPrefix +
                            data.details.businessPhotoFirst.path
                          }
                        />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent md:hidden" />
                    </div>

                    {/* Mobile — title card at top */}
                    <div className="absolute top-0 left-0 right-0 z-20 md:hidden">
                      <div className="mx-auto px-4 pt-6">
                        <div className="bg-[color-mix(in_srgb,var(--surface-primary)_95%,transparent)] backdrop-blur-sm rounded-lg px-4 py-3 shadow-lg border border-border-token-neutral-minimal">
                          <h1 className="title-s-semibold tracking-[-0.1px] mb-1 text-fg-neutral-strong">
                            {data.details.name}
                          </h1>
                          <p className="body-s-regular text-fg-neutral-strong">
                            {data.details.distance} mile |{" "}
                            {data.details.businessType}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Desktop — white bar at bottom of hero */}
                    <div className="hidden md:block absolute bottom-0 left-0 right-0 h-[120px] bg-bg-brand-secondary-default w-full">
                      <div className="max-w-[1440px] mx-auto px-6 h-full flex items-end gap-6 relative w-full">
                        {/* Circular profile image */}
                        <div className="absolute -top-[60px] left-6">
                          <div className="w-[140px] h-[140px] rounded-full border-4 border-white bg-white overflow-hidden">
                            <img
                              alt={data.details.name}
                              className="w-full h-full object-cover"
                              src={
                                data.details.businessPhotoFirst?.path
                                  ? bucketUrlPrefix +
                                    data.details.businessPhotoFirst.path
                                  : "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=140&h=140&fit=crop&crop=center"
                              }
                            />
                          </div>
                        </div>
                        {/* Venue name + type */}
                        <div className="flex-1 ml-[164px] pb-6 pt-4">
                          <div className="title-m-bold tracking-[-0.16px] mb-1">
                            {data.details.name}
                          </div>
                          <p className="body-s-regular">
                            {data.details.distance} mile |{" "}
                            {data.details.businessType}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {data.isSsoProtect === 1 ? (
                    <div className="py-10 flex justify-center">
                      <div className="bg-white rounded-2xl shadow-sm border border-border-token-brand-minimal p-8 flex flex-col items-center gap-3 w-full max-w-lg mx-4">
                        <p className="title-s-semibold text-fg-neutral-strong">
                          {i18n.get("redirectingToLogin")}
                        </p>
                      </div>
                    </div>
                  ) : (
                    /* Password gate */
                    <div className="py-10">
                      <PasswordGate
                        isLoading={isDetailsLoading}
                        hasError={passwordError}
                        onSubmit={(pw) => {
                          setPasswordError(false);
                          setPendingPassword(pw);
                        }}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {error && (
                    <div
                      className={`${!hasNearbyVenues ? "max-w-[1160px] px-4" : "px-8"} mx-auto overflow-hidden`}
                    >
                      <div>{error.message}</div>
                    </div>
                  )}

                  <ComplexVenueSheetContent
                    venueDetailsResponse={data}
                    onClose={() => {}}
                    fullSize
                    onRefresh={() => {
                      setTimeout(() => memoizedLoadVenueDetails(), 1000);
                    }}
                    openAllPhotosInline={false}
                    cid={cid}
                    tid={tid}
                    onNotificationClick={handleNotificationClick}
                  />
                </>
              )}
            </section>
          ) : (
            /* Basic Venue Layout (with map sidebar) */
            <div className="h-below-navbar  bg-bg-brand-secondary-default flex overflow-hidden md:grid md:grid-rows-1 md:grid-cols-[1fr_360px] transition-all duration-300 gap-4 ">
              <div className="flex flex-col min-w-0 h-full">
                {error && <div>{error.message}</div>}
                <VenueSheetContent
                  venueDetailsResponse={data}
                  onBack={() => {
                    console.log("onBack");
                    if (source === "notifications-page") {
                      // Navigate back to notifications page
                      navigate({ to: "/notifications" });
                    } else if (source === "notification-settings") {
                      // Navigate back to notification settings page
                      navigate({ to: "/settings" });
                    } else {
                      setTimeout(() => {
                        setSelectedVenue(null);
                        window.history.pushState({}, "", "/");
                      }, 500);
                    }
                  }}
                  fullSize
                  onRefresh={memoizedLoadVenueDetails}
                  openAllPhotosInline={false}
                  cid={cid}
                  tid={tid}
                  tb={tb}
                  tab={tab}
                  openModal={openModal}
                  notify={notify}
                  currentVenueTypePage={currentVenueTypePage}
                  source={source}
                  onShowDetailView={(initialSpaceId) => {
                    window.location.href = `/accessibility-guide-space/${data.details.business.slug}/${initialSpaceId}`;
                  }}
                  onShowPhotosDetailView={(initialCategoryId) => {
                    setPhotosDetailCategoryId(initialCategoryId);
                    setShowPhotosDetail(true);
                  }}
                />
              </div>

              {/* Map sidebar for basic venues. The Ask AI dock overlays this
                  column when opened (map stays mounted underneath). */}
              <div className="min-w-0 hidden md:block mt-4 mr-4 mb-4 relative">
                <ClientOnly>
                  <BasicMapView
                    query=""
                    currentVenueTypePage={currentVenueTypePage}
                    mapSource="venuedetails"
                  />
                </ClientOnly>
                <VenueAssistantMapDock />
              </div>
            </div>
          )}
        </>
      )}

      {/* Show error message if data failed to load */}
      {!showAccessibilityDetail && !showPhotosDetail && !data && error && (
        <div className="p-8">
          <div>{error.message}</div>
        </div>
      )}

      {/* Interest Notification Modal (for Complex venues) */}
      {selectedVenueForNotification && (
        <InterestNotificationModal
          businessId={selectedVenueForNotification.id}
          businessName={selectedVenueForNotification.name}
          brandId={selectedVenueForNotification.brandId}
          brandName={selectedVenueForNotification.brandName}
          interestId={selectedVenueForNotification.interestId}
          interestedType={selectedVenueForNotification.interestedType}
          notifyAccessibilityInformation={
            selectedVenueForNotification.notifyAccessibilityInformation
          }
          notifyLiveAlert={selectedVenueForNotification.notifyLiveAlert}
          isOpen={isNotificationModalOpen}
          onClose={handleNotificationModalClose}
          onSuccess={() => {
            // Called when the notification is successfully saved
            console.log("Notification interest saved successfully");
          }}
          onComplete={() => {
            // Called when the success modal closes
            // Now we can safely clear the venue data
            setSelectedVenueForNotification(null);
          }}
          alertsCount={
            (data?.details as { businessNotesAll?: unknown[] } | undefined)
              ?.businessNotesAll?.length ?? 0
          }
        />
      )}
    </main>
  );
}
